import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { submitLead } from "../lib/crm";
import heroImg from "../assets/mba_bangalore_hero.png";
import logo from "../assets/logo.webp"; // Assuming this is UniThink logo

// Identifies this form to the CRM: becomes the campaign name and the key its
// Lead Routing rule matches on.
const CRM_FORM_ID = "mba-bangalore";

const colleges = [
  { name: "RVCE", logo: "https://rvce.edu.in/wp-content/uploads/2025/08/Logo-2.png", usp: "Top-ranked for management & engineering excellence." },
  { name: "BMSCE", logo: "https://upload.wikimedia.org/wikipedia/en/thumb/8/87/BMS_College_of_Engineering.svg/1280px-BMS_College_of_Engineering.svg.png", usp: "Legacy institution with strong industry connects." },
  { name: "CHRIST University", logo: "https://christuniversity.in/images/logo.png", usp: "Prestigious university known for holistic development." },
  { name: "Presidency University", logo: "https://upload.wikimedia.org/wikipedia/en/8/89/Presidency_University%2C_Bangalore_logo.svg", usp: "Modern campus with industry-aligned MBA programs." },
  { name: "REVA University", logo: "https://www.reva.edu.in/icgtasmt-2022/assets/pages/img/Logo/Reva_logo.jpeg", usp: "Focus on innovation and research-driven learning." },
  { name: "Alliance University", logo: "https://upload.wikimedia.org/wikipedia/en/9/90/Alliance_University_Icon.svg", usp: "Global exposure and international collaborations." },
  { name: "New Horizon", logo: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRddrEUVjZBl5qVSGd7b_xONPgZXYO2HVwIFw&s", usp: "Excellent placement record and corporate tie-ups." },
  { name: "Jain University", logo: "https://48237376.fs1.hubspotusercontent-na1.net/hubfs/48237376/manabuki/universities%20logo/jain-university--.jpeg", usp: "Entrepreneurial mindset and innovative curriculum." },
  { name: "St. Joseph's Commerce", logo: "https://sjcc.edu.in/images/college_sjc.webp", usp: "Specialized in commerce and management excellence." },
  { name: "St. Joseph's University", logo: "https://upload.wikimedia.org/wikipedia/commons/5/5c/St._Joseph%27s_University_Crest.webp", usp: "Legacy of academic brilliance and social responsibility." },
  { name: "CMRIT", logo: "https://www.cmrit.ac.in/wp-content/uploads/2021/07/CMRIT-NEW-LOGO-03-220x150-1.png", usp: "Focus on technology-driven management education." },
  { name: "ISME", logo: "https://www.isme.in/wp-content/uploads/2020/11/Globe-and-ISME-Logo.png", usp: "International standards and global career paths." },
  { name: "St. George College", logo: "https://stgeorgecollege.org.in/assets/images/logo.webp", usp: "Commitment to quality education and student growth." },
  { name: "S-VYASA", logo: "https://f2.leadsquaredcdn.com/t/t20250124170053/content/common/images/S-VYASA%20Logo.png", usp: "Unique blend of management and holistic wellness." },
];

const reasons = [
  { title: "Silicon Valley of India", desc: "Bengaluru is home to 13,000+ startups and 80% of global IT firms, offering unparalleled networking." },
  { title: "Top-Tier Placements", desc: "Average packages in Bengaluru's top B-schools range from ₹8 LPA to ₹25 LPA+ with marquee recruiters." },
  { title: "Innovation Hub", desc: "Learn in an environment where innovation is in the air. Direct proximity to industry leaders and tech parks." },
  { title: "Global Ecosystem", desc: "Bengaluru's cosmopolitan culture and global business presence provide an international learning experience." },
];

export default function MbaInBangalore() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ fullName: "", phone: "", email: "", city: "", collegePreference: "", otherCollegeName: "", message: "", _gotcha: "" });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ ok: null, msg: "" });

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ ok: null, msg: "" });

    // Validation
    if (!form.fullName || !form.phone || !form.email || !form.message.trim()) {
      setStatus({ ok: false, msg: "Please fill all required fields, including your message." });
      return;
    }

    const cleanPhone = form.phone.replace(/[\s-]/g, "");
    if (!/^[6-9][0-9]{9}$/.test(cleanPhone)) {
      setStatus({ ok: false, msg: "Valid 10-digit phone number is required." });
      return;
    }

    if (form.message.trim().length < 10) {
      setStatus({ ok: false, msg: "Please share at least a sentence (10+ characters) about your goals." });
      return;
    }

    try {
      setSubmitting(true);
      const finalCollege = form.collegePreference === "Other" ? `Other: ${form.otherCollegeName}` : form.collegePreference;

      // The CRM is the only destination for this form, so a failure here loses
      // the lead. The generous timeout and single retry cover a server that has
      // gone to sleep and needs a moment to wake; retrying is safe because the
      // CRM de-duplicates on phone and email.
      const result = await submitLead({
        formId: CRM_FORM_ID,
        timeoutMs: 30000,
        retries: 1,
        fields: {
          fullName: form.fullName,
          phone: cleanPhone,
          email: form.email,
          city: form.city,
          collegePreference: finalCollege,
          message: form.message,
          _gotcha: form._gotcha,
        },
      });

      if (!result.ok) {
        // Leave what they typed in place so they can retry without starting over.
        console.warn("[crm] lead not recorded:", result.reason);
        setStatus({
          ok: false,
          msg: "We couldn't submit your details just now. Please try again, or WhatsApp us if it keeps failing.",
        });
        return;
      }

      setStatus({ ok: true, msg: "Registration successful! We will contact you soon." });
      setForm({ fullName: "", phone: "", email: "", city: "", collegePreference: "", otherCollegeName: "", message: "", _gotcha: "" });
    } catch {
      setStatus({ ok: false, msg: "Error submitting form. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="font-sans text-gray-900 bg-gray-50 min-h-screen">
      {/* Hero Section with Form */}
      <section className="relative min-h-[90vh] flex items-center pt-24 pb-12 overflow-hidden">
        <img
          src={heroImg}
          alt="Bangalore MBA"
          className="absolute inset-0 w-full h-full object-cover brightness-[0.4]"
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 w-full flex flex-col lg:flex-row items-center gap-12">
          {/* Hero Text */}
          <div className="lg:w-3/5 text-white">
            <h1 className="text-4xl md:text-7xl font-extrabold mb-6 leading-tight animate-fade-in-up">
              Master Your Career <br /> with an <span className="text-blue-400">MBA in Bengaluru</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-200 font-light leading-relaxed">
              Secure your future in the Silicon Valley of India. Explore top-tier colleges, get expert guidance, and accelerate your growth.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <span className="bg-white/10 backdrop-blur-md px-4 py-2 rounded-full text-sm border border-white/20">✓ 100% Admission Help</span>
              <span className="bg-white/10 backdrop-blur-md px-4 py-2 rounded-full text-sm border border-white/20">✓ Top B-Schools</span>
              <span className="bg-white/10 backdrop-blur-md px-4 py-2 rounded-full text-sm border border-white/20">✓ Career Coaching</span>
            </div>
          </div>

          {/* Registration Form */}
          <div className="lg:w-2/5 w-full">
            <div className="bg-white p-6 md:p-8 rounded-3xl shadow-2xl transform transition-all hover:scale-[1.02]">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-800">Register Your Interest</h3>
                <p className="text-gray-500 text-sm mt-1 font-medium italic">Get free expert counselling today!</p>
              </div>
              <form onSubmit={onSubmit} className="space-y-4">
                {/* Bot trap: hidden from people, so anything typed here is spam. */}
                <input
                  type="text"
                  name="_gotcha"
                  value={form._gotcha}
                  onChange={onChange}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                />
                <input
                  type="text"
                  name="fullName"
                  placeholder="Full Name *"
                  value={form.fullName}
                  onChange={onChange}
                  className="w-full p-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 transition-all"
                  required
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number *"
                  value={form.phone}
                  onChange={onChange}
                  className="w-full p-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 transition-all"
                  required
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address *"
                  value={form.email}
                  onChange={onChange}
                  className="w-full p-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 transition-all"
                  required
                />
                <input
                  type="text"
                  name="city"
                  placeholder="Your City"
                  value={form.city}
                  onChange={onChange}
                  className="w-full p-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 transition-all"
                />
                <select
                  name="collegePreference"
                  value={form.collegePreference}
                  onChange={onChange}
                  className="w-full p-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 transition-all cursor-pointer"
                >
                  <option value="">Preferred College (Optional)</option>
                  {colleges.map((c, i) => <option key={i} value={c.name}>{c.name}</option>)}
                  <option value="Other">Other</option>
                </select>

                {form.collegePreference === "Other" && (
                  <input
                    type="text"
                    name="otherCollegeName"
                    placeholder="Mention your interested college *"
                    value={form.otherCollegeName}
                    onChange={onChange}
                    className="w-full p-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 transition-all animate-fade-in"
                    required
                  />
                )}
                <textarea
                  name="message"
                  placeholder="Your message *  — share your goals, current background, and any specific questions"
                  value={form.message}
                  onChange={onChange}
                  rows={4}
                  required
                  minLength={10}
                  className="w-full p-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 transition-all resize-none"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-xl shadow-lg transition-all transform active:scale-95 disabled:opacity-50"
                >
                  {submitting ? "Submitting..." : "Get Free Guidance"}
                </button>
                {status.msg && (
                  <p className={`text-center text-sm font-semibold animate-pulse ${status.ok ? "text-green-600" : "text-red-500"}`}>
                    {status.msg}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Why Bangalore Section */}
      <section className="py-20 px-4 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-800">Why Bengaluru for your MBA?</h2>
          <div className="w-24 h-1 bg-blue-600 mx-auto mt-4 rounded-full"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {reasons.map((r, i) => (
            <div key={i} className="bg-white p-8 rounded-2xl shadow-lg border-b-4 border-blue-600 hover:transform hover:-translate-y-2 transition-all">
              <h3 className="text-xl font-bold mb-4 text-blue-600">{r.title}</h3>
              <p className="text-gray-600 leading-relaxed">{r.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured Colleges */}
      <section className="py-20 bg-white px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-800">Top Featured Colleges</h2>
            <p className="text-gray-500 mt-4">We help you get admitted into these world-class institutions</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-8">
            {colleges.map((c, i) => (
              <div key={i} className="group flex flex-col items-center p-6 border border-gray-100 rounded-2xl bg-gray-50 hover:bg-white hover:shadow-xl transition-all">
                <div className="h-20 w-full flex items-center justify-center mb-4 transition-transform group-hover:scale-110">
                  <img src={c.logo} alt={c.name} className="max-h-full max-w-full object-contain transition-all" />
                </div>
                <h4 className="font-bold text-center text-gray-800">{c.name}</h4>
                <p className="text-xs text-center text-gray-500 mt-2 opacity-0 group-hover:opacity-100 transition-all">{c.usp}</p>
              </div>
            ))}
            {/* And Many More Card */}
            <div
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-blue-200 rounded-2xl bg-blue-50/50 hover:bg-blue-50 hover:border-blue-400 transition-all cursor-pointer group"
            >
              <div className="text-4xl text-blue-600 font-bold mb-2 group-hover:scale-110 transition-transform">+50</div>
              <h4 className="font-bold text-center text-blue-800 text-lg leading-tight">And many more Colleges</h4>
              <p className="text-[10px] text-center text-blue-600 mt-2 font-medium">Click to enquire about your preferred institution</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-blue-600 text-white text-center">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-bold mb-6 italic italic">Don't wait for your dream career!</h2>
          <p className="text-xl opacity-90 mb-8">
            Our expert counselors are ready to help you navigate the complex admission landscape of Bengaluru.
          </p>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="bg-white text-blue-600 font-bold px-10 py-4 rounded-full text-lg shadow-xl hover:bg-gray-100 transition-all"
          >
            Register Now
          </button>
        </div>
      </section>

      {/* Quick Contact Footer */}

    </div>
  );
}
